import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { useTheme, useMediaQuery } from "@mui/material";
import { useTranslationApp } from "../../../lib/i18next";
import { logoPlatform } from "../../../utils/string";
import { DRAWER_CAMPAIGN } from "../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import {
  CAMPAIGN_APPROVAL_SIDEBAR_ADS,
  CAMPAIGN_APPROVAL_SIDEBAR_ADS_SINGLE,
  CAMPAIGN_APPROVAL_SIDEBAR_AD_SETS,
  CAMPAIGN_APPROVAL_SIDEBAR_AD_SETS_SINGLE,
  CAMPAIGN_APPROVAL_SIDEBAR_CAMPAIGNS,
  CAMPAIGN_APPROVAL_SIDEBAR_CREATIVES,
  CAMPAIGN_APPROVAL_SIDEBAR_CREATIVES_SINGLE,
  CAMPAIGN_APPROVAL_SIDEBAR_TEXTS,
  CAMPAIGN_APPROVAL_SIDEBAR_TEXTS_SINGLE,
  DRAFT,
} from "../../../i18n/keysTranslations";
import { countPlatformSummary, formatApprovalCountLabel } from "../utils";
import ApprovalCurrentReviewerBanner from "../../../components/ContentApproval/ApprovalCurrentReviewerBanner";

const SIDEBAR_WIDTH = DRAWER_CAMPAIGN;

const ApprovalSidebar = ({
  platforms = [],
  selectedPlatform = "",
  onSelectPlatform,
  clientName = "",
  mobileOpen = false,
  onMobileClose,
  currentReviewer = null,
  reviewers = [],
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const renderPlatformCard = (platformData) => {
    const isSelected = platformData.platform === selectedPlatform;
    const summary =
      platformData.summary || countPlatformSummary(platformData.entities);
    const campaignTitle =
      platformData.campaignName || platformData.platform || "";

    return (
      <Box
        key={platformData.platform}
        component="button"
        type="button"
        onClick={() => onSelectPlatform?.(platformData.platform)}
        sx={{
          width: "100%",
          textAlign: "left",
          border: "1px solid",
          borderColor: isSelected ? "#000" : INPUT_COLOR_BORDER,
          borderRadius: 2.5,
          p: 1.5,
          bgcolor: "background.paper",
          cursor: "pointer",
          transition: "border-color 0.2s ease",
          "&:hover": {
            borderColor: isSelected ? "#000" : INPUT_COLOR_BORDER,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Avatar
            src={logoPlatform(platformData.platform)}
            variant="square"
            sx={{
              width: 24,
              height: 24,
              bgcolor: "transparent",
              "& .MuiAvatar-img": {
                objectFit: "contain",
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {t(DRAFT)}
          </Typography>
        </Box>

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mb: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {campaignTitle}
        </Typography>

        {clientName ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {clientName}
          </Typography>
        ) : null}

        <Typography variant="caption" color="text.secondary">
          {[
            formatApprovalCountLabel(
              summary.adSets,
              CAMPAIGN_APPROVAL_SIDEBAR_AD_SETS,
              CAMPAIGN_APPROVAL_SIDEBAR_AD_SETS_SINGLE,
              t
            ),
            formatApprovalCountLabel(
              summary.ads,
              CAMPAIGN_APPROVAL_SIDEBAR_ADS,
              CAMPAIGN_APPROVAL_SIDEBAR_ADS_SINGLE,
              t
            ),
            formatApprovalCountLabel(
              summary.texts,
              CAMPAIGN_APPROVAL_SIDEBAR_TEXTS,
              CAMPAIGN_APPROVAL_SIDEBAR_TEXTS_SINGLE,
              t
            ),
            formatApprovalCountLabel(
              summary.creatives,
              CAMPAIGN_APPROVAL_SIDEBAR_CREATIVES,
              CAMPAIGN_APPROVAL_SIDEBAR_CREATIVES_SINGLE,
              t
            ),
          ].join(" · ")}
        </Typography>
      </Box>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        borderRight: `1px solid ${INPUT_COLOR_BORDER}`,
      }}
    >
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <ApprovalCurrentReviewerBanner
          currentReviewer={currentReviewer}
          reviewers={reviewers}
          showReviewerIcon={false}
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: "0.08em",
          }}
        >
          {t(CAMPAIGN_APPROVAL_SIDEBAR_CAMPAIGNS)}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {platforms.map(renderPlatformCard)}
      </Box>
    </Box>
  );

  if (isTablet) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        anchor="left"
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: "100%",
        alignSelf: "stretch",
      }}
    >
      {sidebarContent}
    </Box>
  );
};

//eslint-disable-next-line
export { SIDEBAR_WIDTH };
export default ApprovalSidebar;
